"use client";

import React from "react";
import { motion, useInView } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import HumanRingIcon from "@/components/global/ui/HumanRingIcon";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import EmotionNodes from "./EmotionNodes";
import {
  EMOTION_GRAPH_ASPECT_DESKTOP,
  EMOTION_GRAPH_ASPECT_MOBILE,
  EMOTION_NODES_DESKTOP,
  EMOTION_NODES_MOBILE,
} from "./emotionNodesData";

/**
 * "Nothing changed in the market, but something changed in you."
 * Figma: desktop 3165:28548, mobile 3205:60970.
 * Replaces ScrollEncryptSection on the homepage (the old section still
 * renders on /homepage-old).
 */

const EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const reveal = (y: number, delay = 0) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.7, ease: [...EASING], delay },
});

// Copy from the Figma frames, shown until the Emotions fields are filled in Studio
const FALLBACK_CHIP = "Launch yearly discount -16% off";
const FALLBACK_TEXT =
  "Emotions, bias and pressure make or break every trade — most traders never see it, because they’re not tracking it.";
const FALLBACK_CTA_TEXT = "Start 14-day free trial";
const FALLBACK_HINT = "(Tap any node to learn)";

// Vertical band from the Figma "Orange BG": black until ~half the section,
// then orange, ending white at the very bottom.
const BACKGROUND =
  "linear-gradient(180deg, #040404 0%, #050200 48%, #44170a 58%, #c14000 66%, #ff5500 72%, #ff6d24 78%, #ff8548 84%, #ffa87d 90%, #ffc5a8 94%, #ffe2d4 97%, #ffffff 100%)";
const BACKGROUND_MOBILE =
  "linear-gradient(180deg, #040404 0%, #040404 41%, #782600 62%, #ff5500 80%, #ffffff 95%, #ffffff 100%)";

function FallbackTitle() {
  return (
    <h2>
      Nothing changed
      <br className="lg:hidden" /> in the market.
      <span className="lg:hidden">
        <HumanRingIcon />
      </span>
      <br className="hidden lg:block" /> but something{" "}
      <span className="hidden lg:inline">
        <HumanRingIcon />
      </span>{" "}
      changed
      <br className="lg:hidden" /> in you.
    </h2>
  );
}

function Graph({ compact }: { compact: boolean }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });
  return (
    <div
      ref={ref}
      className="w-full">
      <EmotionNodes
        nodes={compact ? EMOTION_NODES_MOBILE : EMOTION_NODES_DESKTOP}
        aspectRatio={compact ? EMOTION_GRAPH_ASPECT_MOBILE : EMOTION_GRAPH_ASPECT_DESKTOP}
        revealed={inView}
        compact={compact}
      />
    </div>
  );
}

export default function EmotionsSection({ data }: { data: PageHomeSchema }) {
  const { emotionsChip, emotionsTitle, emotionsText, emotionsCTA, emotionsNodeHint, heroCTA } = data;
  const cta = emotionsCTA?.cta ? emotionsCTA : heroCTA;
  const ctaText = emotionsCTA?.cta ? undefined : FALLBACK_CTA_TEXT;

  return (
    <section
      id="emotions"
      className="relative z-[1] w-full overflow-x-clip bg-[#040404] text-white">
      {/* Background band + noise */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 lg:hidden"
        style={{ background: BACKGROUND_MOBILE }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden lg:block"
        style={{ background: BACKGROUND }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-[40%] bottom-0 bg-[url('/noise.svg?v=4')] bg-repeat opacity-50 mix-blend-soft-light"
      />

      <div className="relative z-[2] mx-auto flex w-full flex-col items-center gap-[9rem] px-[3rem] pt-[9rem] pb-[5rem] lg:gap-[8.75rem] lg:px-0 lg:py-[20.5rem]">
        {/* Header */}
        <div className="flex w-full flex-col items-center gap-[1.5rem] lg:gap-[2rem]">
          <div className="flex w-full flex-col items-center gap-[2.25rem] lg:gap-[2rem]">
            <motion.div {...reveal(24)}>
              <Chip
                text={emotionsChip || FALLBACK_CHIP}
                className="gap-[1.5rem] prose-p:text-white"
              />
            </motion.div>

            <motion.div
              {...reveal(32, 0.1)}
              className="richtext richtext-center w-full lg:w-[125rem] prose-h1:m-0 prose-h1:text-[5rem] prose-h1:leading-[104%] prose-h1:font-normal prose-h1:tracking-[-0.15rem] prose-h1:text-white lg:prose-h1:bg-gradient-to-b lg:prose-h1:from-white lg:prose-h1:to-[#CFCFCF] lg:prose-h1:bg-clip-text lg:prose-h1:text-[8rem] lg:prose-h1:leading-[110%] lg:prose-h1:tracking-normal lg:prose-h1:text-transparent prose-h2:m-0 prose-h2:text-[5rem] prose-h2:leading-[104%] prose-h2:font-normal prose-h2:tracking-[-0.15rem] prose-h2:text-white lg:prose-h2:bg-gradient-to-b lg:prose-h2:from-white lg:prose-h2:to-[#CFCFCF] lg:prose-h2:bg-clip-text lg:prose-h2:text-[8rem] lg:prose-h2:leading-[110%] lg:prose-h2:tracking-normal lg:prose-h2:text-transparent">
              {emotionsTitle?.length ? (
                <PortableText
                  value={emotionsTitle}
                  components={richTextComponent}
                />
              ) : (
                <FallbackTitle />
              )}
            </motion.div>
          </div>

          <motion.p
            {...reveal(28, 0.2)}
            className="m-0 w-full text-center text-[2rem] leading-[150%] text-white/70 lg:w-[72.75rem] lg:text-[2.25rem]">
            {emotionsText || FALLBACK_TEXT}
          </motion.p>

          {cta && (
            <motion.div
              {...reveal(28, 0.3)}
              className="mt-[1.5rem] w-full lg:mt-[2rem] lg:w-fit">
              <CTAButton
                data={cta}
                overrideText={ctaText}
                className="[&_.cta-wrapper]:w-full [&_.button]:py-[1.5rem] lg:[&_.button]:py-[2rem]"
              />
            </motion.div>
          )}
        </div>

        {/* Node graph: mobile */}
        <div className="flex w-full flex-col gap-[3rem] lg:hidden">
          <p className="m-0 text-center font-spacemono text-[1.75rem] leading-[150%] text-white/60 uppercase">
            {emotionsNodeHint || FALLBACK_HINT}
          </p>
          <Graph compact />
        </div>

        {/* Node graph: desktop */}
        <div className="hidden w-full lg:block">
          <Graph compact={false} />
        </div>
      </div>
    </section>
  );
}
